
// Hardcoded API Key as requested for immediate stability
const API_KEY = "AIzaSyAhmGuUlKbNw3XNssAXUfVOf01j2gSYSsU";

// STRICTLY USE GEMINI 3 PRO IMAGE PREVIEW - NO FALLBACKS
const MODEL_ID = 'gemini-3-pro-image-preview';
const BASE_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent`;

// Helper to convert blob to base64
export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      // Remove data url prefix (e.g. "data:image/png;base64,")
      const base64Data = base64String.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Helper to fetch the locked image
export const fetchLockedImageBase64 = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) throw new Error("Failed to fetch locked artifact");
    const blob = await response.blob();
    return blobToBase64(blob);
  } catch (error) {
    console.error("Fetch Locked Image Error:", error);
    return ""; 
  }
};

/**
 * Generates a RASTER IMAGE using gemini-3-pro-image-preview.
 * Uses native fetch for Vercel compatibility.
 */
const generateImageContent = async (fullPrompt: string, imageBase64: string | null): Promise<string> => {
  if (!API_KEY) {
    throw new Error("API Key is missing.");
  }

  // Construct Payload for Multimodal Request
  const parts: any[] = [
    { text: fullPrompt }
  ];

  if (imageBase64) {
    parts.push({
      inlineData: {
        mimeType: 'image/png',
        data: imageBase64
      }
    });
  }

  const payload = {
    contents: [{ parts }],
  };

  try {
    const response = await fetch(`${BASE_URL}?key=${API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errText = await response.text();
      console.error("Gemini API Error Details:", errText);
      throw new Error(`Gemini API Error: ${response.status} - ${errText}`);
    }

    const data = await response.json();
    const candidate = data.candidates?.[0];

    if (!candidate?.content?.parts) {
      throw new Error("Model returned no content.");
    }

    // Iterate through parts to find the image
    let imagePart = null;
    for (const part of candidate.content.parts) {
      if (part.inlineData) {
        imagePart = part.inlineData;
        break;
      }
    }

    if (!imagePart) {
      const textPart = candidate.content.parts.find((p: any) => p.text);
      if (textPart) {
        throw new Error(`Model refused: ${textPart.text}`);
      }
      throw new Error("Model generated text instead of an image. Try again.");
    }

    return `data:${imagePart.mimeType};base64,${imagePart.data}`;

  } catch (error) {
    console.error("Generation Error:", error);
    throw error;
  }
};

// SCENE GENERATOR (Meme / Money Printer)
export const generateOlekMeme = async (userPrompt: string, base64Reference: string): Promise<string> => {
  const prompt = `
    You are an expert satirical artist.
    
    CHARACTER DETAILS:
    - Name: Olek
    - Archetype: Hedonistic Wojak, Rich, Successful, Arrogant.
    - Appearance: Stick figure style but wearing a high-end Pink Suit. Receding Pink Hair. Smug facial expression.
    
    STYLE INSTRUCTIONS:
    - Art Style: Crude MS Paint / Internet Meme aesthetic.
    - Lines: Thick, pixelated or rough black lines.
    - Colors: Bright Pink (#ff99cc) for the suit, Money Green accents. White background.
    - Vibe: Low quality, high effort.
    
    ACTION:
    - The character Olek is: ${userPrompt}
    - Keep the character consistent with the reference provided.
    
    Generate a single image.
  `;

  return await generateImageContent(prompt, base64Reference);
};

// PFP GENERATOR (Fake ID / Identity Theft)
export const generateOlekPfp = async (traits: string, base64Reference: string): Promise<string> => {
  const prompt = `
    You are an expert avatar creator.
    
    TASK:
    - Edit the provided reference image of Olek (Pink Suit, Pink Hair, Smug Face).
    - Maintain the exact composition (Head and Shoulders profile).
    - ADD the following traits/accessories: ${traits}
    
    STYLE:
    - MS Paint, Meme, Crude, "Bad Art".
    - Keep the white background.
    
    Generate the modified avatar image.
  `;
          
  return await generateImageContent(prompt, base64Reference);
};
